import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { getAuthHeaders } from "../utils/getAuthHeaders";
import { getErrorMessage } from "../utils/getErrorMessage";
import {
  urlAgentsDashboardAdminThumbnail,
  urlAgentsDashboardAdminThumbnailUpload,
} from "../config/api";

const MAX_FILE_BYTES = 10 * 1024 * 1024;

// Admin fallback thumbnail manager. The backend serves SDK captures with
// priority; this only kicks in when the dashboard's SDK path never runs.
const DashboardThumbnailModal = ({ dashboard, onClose }) => {
  const dashboardId = dashboard?.id;

  const [existingUrl, setExistingUrl] = useState(null);
  const [hasExisting, setHasExisting] = useState(false);
  const [checkingExisting, setCheckingExisting] = useState(true);

  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const objectUrlRef = useRef(null);
  const existingObjectUrlRef = useRef(null);

  const revokePreview = useCallback(() => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  }, []);

  const revokeExisting = useCallback(() => {
    if (existingObjectUrlRef.current) {
      URL.revokeObjectURL(existingObjectUrlRef.current);
      existingObjectUrlRef.current = null;
    }
  }, []);

  const loadExisting = useCallback(async () => {
    if (!dashboardId) return;
    setCheckingExisting(true);
    setErrorMessage("");
    try {
      const response = await axios.get(urlAgentsDashboardAdminThumbnail(dashboardId), {
        headers: { ...getAuthHeaders() },
        responseType: "blob",
        // Treat 404 as "no admin thumbnail stored" rather than an error.
        validateStatus: (status) => (status >= 200 && status < 300) || status === 404,
      });

      revokeExisting();

      if (response.status === 404) {
        setExistingUrl(null);
        setHasExisting(false);
        return;
      }

      const objectUrl = URL.createObjectURL(response.data);
      existingObjectUrlRef.current = objectUrl;
      setExistingUrl(objectUrl);
      setHasExisting(true);
    } catch (err) {
      setExistingUrl(null);
      setHasExisting(false);
      setErrorMessage(getErrorMessage(err, "Failed to load current thumbnail."));
    } finally {
      setCheckingExisting(false);
    }
  }, [dashboardId, revokeExisting]);

  useEffect(() => {
    loadExisting();
    return () => {
      revokePreview();
      revokeExisting();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardId]);

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    setErrorMessage("");
    setSuccessMessage("");

    if (!file) {
      revokePreview();
      setSelectedFile(null);
      setPreviewUrl(null);
      return;
    }

    if (!file.type.startsWith("image/")) {
      revokePreview();
      setSelectedFile(null);
      setPreviewUrl(null);
      setErrorMessage("Please pick an image file (png/jpg/webp).");
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      revokePreview();
      setSelectedFile(null);
      setPreviewUrl(null);
      setErrorMessage("Image must be 10 MB or smaller.");
      return;
    }

    revokePreview();
    const url = URL.createObjectURL(file);
    objectUrlRef.current = url;
    setSelectedFile(file);
    setPreviewUrl(url);
  };

  const handleUpload = async () => {
    if (!selectedFile || !dashboardId) return;

    const authHeaders = getAuthHeaders();
    if (!authHeaders.Authorization) {
      setErrorMessage("Please log in to upload a thumbnail.");
      return;
    }

    setUploading(true);
    setErrorMessage("");
    setSuccessMessage("");

    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      await axios.post(
        urlAgentsDashboardAdminThumbnailUpload(dashboardId),
        formData,
        { headers: { ...authHeaders } },
      );
      setSuccessMessage("Thumbnail uploaded.");
      revokePreview();
      setSelectedFile(null);
      setPreviewUrl(null);
      await loadExisting();
    } catch (err) {
      setErrorMessage(getErrorMessage(err, "Failed to upload thumbnail."));
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async () => {
    if (!dashboardId) return;
    if (!window.confirm("Remove the admin fallback thumbnail for this dashboard?")) return;

    setRemoving(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      await axios.delete(urlAgentsDashboardAdminThumbnail(dashboardId), {
        headers: { ...getAuthHeaders() },
      });
      setSuccessMessage("Thumbnail removed.");
      await loadExisting();
    } catch (err) {
      setErrorMessage(getErrorMessage(err, "Failed to remove thumbnail."));
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={headerStyle}>
          <div>
            <h2 style={titleStyle}>Dashboard thumbnail</h2>
            <p style={subtitleStyle}>
              {dashboard?.title ? `${dashboard.title} · ID ${dashboardId}` : `ID ${dashboardId}`}
            </p>
          </div>
          <button type="button" onClick={onClose} style={closeButtonStyle}>Close</button>
        </div>

        <p style={helpStyle}>
          Uploaded here as a <strong>fallback</strong>. If the dashboard&rsquo;s SDK screenshot is
          working, that fresh capture is served first — the admin thumbnail only shows for
          dashboards whose SDK path never runs.
        </p>

        {errorMessage && <div style={errorMessageStyle}>{errorMessage}</div>}
        {successMessage && <div style={successMessageStyle}>{successMessage}</div>}

        <div style={sectionStyle}>
          <h3 style={sectionHeadingStyle}>Current admin thumbnail</h3>
          {checkingExisting ? (
            <div style={emptyStyle}>Loading&hellip;</div>
          ) : hasExisting && existingUrl ? (
            <div>
              <img src={existingUrl} alt="Current admin thumbnail" style={imageStyle} />
              <button
                type="button"
                onClick={handleRemove}
                disabled={removing}
                style={{
                  ...dangerButtonStyle,
                  opacity: removing ? 0.6 : 1,
                  cursor: removing ? "not-allowed" : "pointer",
                }}
              >
                {removing ? "Removing…" : "Remove"}
              </button>
            </div>
          ) : (
            <div style={emptyStyle}>No admin thumbnail stored for this dashboard.</div>
          )}
        </div>

        <div style={sectionStyle}>
          <h3 style={sectionHeadingStyle}>Upload new thumbnail</h3>
          <input
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            style={fileInputStyle}
          />
          {previewUrl && (
            <div style={{ marginTop: "12px" }}>
              <p style={previewLabelStyle}>Preview</p>
              <img src={previewUrl} alt="New thumbnail preview" style={imageStyle} />
            </div>
          )}
          <button
            type="button"
            onClick={handleUpload}
            disabled={!selectedFile || uploading}
            style={{
              ...primaryButtonStyle,
              opacity: !selectedFile || uploading ? 0.6 : 1,
              cursor: !selectedFile || uploading ? "not-allowed" : "pointer",
              marginTop: "12px",
            }}
          >
            {uploading ? "Uploading…" : hasExisting ? "Replace thumbnail" : "Upload thumbnail"}
          </button>
        </div>
      </div>
    </div>
  );
};

const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(15, 23, 42, 0.55)",
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "center",
  padding: "48px 16px",
  zIndex: 1000,
  overflowY: "auto",
};

const modalStyle = {
  background: "#ffffff",
  borderRadius: "10px",
  padding: "24px",
  width: "min(560px, 100%)",
  boxShadow: "0 20px 40px rgba(15, 23, 42, 0.25)",
  color: "#111827",
};

const headerStyle = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: "16px",
  marginBottom: "12px",
};

const titleStyle = {
  margin: 0,
  fontSize: "20px",
  color: "#3730a3",
  fontWeight: 700,
};

const subtitleStyle = {
  margin: "4px 0 0",
  fontSize: "12px",
  color: "#6b7280",
};

const helpStyle = {
  margin: "0 0 16px",
  fontSize: "13px",
  color: "#374151",
  lineHeight: 1.45,
};

const sectionStyle = {
  border: "1px solid #e5e7eb",
  borderRadius: "8px",
  padding: "16px",
  marginBottom: "16px",
};

const sectionHeadingStyle = {
  margin: "0 0 12px",
  fontSize: "14px",
  fontWeight: 600,
  color: "#111827",
};

const imageStyle = {
  display: "block",
  maxWidth: "100%",
  maxHeight: "260px",
  borderRadius: "6px",
  border: "1px solid #e5e7eb",
  background: "#f9fafb",
};

const previewLabelStyle = {
  margin: "0 0 6px",
  fontSize: "12px",
  color: "#6b7280",
  fontWeight: 600,
};

const emptyStyle = {
  fontSize: "13px",
  color: "#6b7280",
  fontStyle: "italic",
};

const fileInputStyle = {
  width: "100%",
  padding: "10px",
  fontSize: "13px",
  border: "1px dashed #cbd5e1",
  borderRadius: "6px",
  background: "#ffffff",
  cursor: "pointer",
};

const primaryButtonStyle = {
  padding: "10px 18px",
  background: "#3730a3",
  color: "#ffffff",
  border: "none",
  borderRadius: "6px",
  fontSize: "13px",
  fontWeight: 600,
};

const dangerButtonStyle = {
  marginTop: "12px",
  padding: "8px 14px",
  background: "#fef2f2",
  color: "#b91c1c",
  border: "1px solid #dc2626",
  borderRadius: "6px",
  fontSize: "13px",
  fontWeight: 600,
};

const closeButtonStyle = {
  padding: "8px 14px",
  background: "#ffffff",
  color: "#111827",
  border: "1px solid #d1d5db",
  borderRadius: "6px",
  fontSize: "13px",
  cursor: "pointer",
};

const successMessageStyle = {
  padding: "10px 12px",
  background: "#d1fae5",
  color: "#065f46",
  border: "1px solid #10b981",
  borderRadius: "6px",
  fontSize: "13px",
  marginBottom: "12px",
};

const errorMessageStyle = {
  padding: "10px 12px",
  background: "#fee2e2",
  color: "#991b1b",
  border: "1px solid #ef4444",
  borderRadius: "6px",
  fontSize: "13px",
  marginBottom: "12px",
};

export default DashboardThumbnailModal;
